import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
  employees: [],
  columns: [
    "id",
    "firstName",
    "lastName",
    "middleName",
    "suffix",
    "dob",
    "createdBy",
    "updatedBy",
    "deletedBy",
    "isDeleted",
    "createdAt",
    "updatedAt",
  ],
  loading: false,
  error: null,
  total: 0,
};

const FETCH_EMPLOYEES_REQUEST = "FETCH_EMPLOYEES_REQUEST";
const FETCH_EMPLOYEES_SUCCESS = "FETCH_EMPLOYEES_SUCCESS";
const FETCH_EMPLOYEES_FAILURE = "FETCH_EMPLOYEES_FAILURE";
const CREATE_EMPLOYEE_REQUEST = "CREATE_EMPLOYEE_REQUEST";
const CREATE_EMPLOYEE_SUCCESS = "CREATE_EMPLOYEE_SUCCESS";
const CREATE_EMPLOYEE_FAILURE = "CREATE_EMPLOYEE_FAILURE";
const UPDATE_EMPLOYEE_REQUEST = "UPDATE_EMPLOYEE_REQUEST";
const UPDATE_EMPLOYEE_SUCCESS = "UPDATE_EMPLOYEE_SUCCESS";
const UPDATE_EMPLOYEE_FAILURE = "UPDATE_EMPLOYEE_FAILURE";
const DELETE_EMPLOYEE_REQUEST = "DELETE_EMPLOYEE_REQUEST";
const DELETE_EMPLOYEE_SUCCESS = "DELETE_EMPLOYEE_SUCCESS";
const DELETE_EMPLOYEE_FAILURE = "DELETE_EMPLOYEE_FAILURE";

export const getEmployees =
  (
    page = 0,
    pageSize = 20,
    sortField = "id",
    sortDirection = "asc",
    filters = {},
    userAgency
  ) =>
  async (dispatch) => {
    const sortFieldMap = {
      "rankInfo.description": "rank",
      "titlePositionInfo.description": "titlePosition",
      "sectionInfo.description": "section",
      "departmentInfo.description": "department",
    };

    sortField = sortFieldMap[sortField] || sortField;

    const service = getService(serviceNames.personnel);
    const query = {
      $limit: pageSize,
      $skip: page * pageSize,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
      // lastName: { $like: '%Sharma%'}, // for searching in the fields
      isDeleted: false,
      agencyId: userAgency,
    };

    const textFields = [
      "employeeNum",
      "ssn",
      "lastName",
      "firstName",
      "middleName",
      "cellNo",
      "separationCode",
    ];

    textFields.forEach((field) => {
      const value = filters[field];
      if (value) {
        query[field] = filters.exactMatch ? value : { $like: `%${value}%` };
      }
    });

    const getQueryValue = (input) => {
      if (Array.isArray(input)) {
        const values = input.map((i) => i.value).filter(Boolean);
        return values.length > 0 ? { $in: values } : undefined;
      } else if (input?.value) {
        return input.value;
      }
      return undefined;
    };

    const multiSelectFields = [
      "status",
      "department",
      "titlePosition",
      "rank",
      "section",
      "residentialCounty",
      "assignedCounty",
    ];

    multiSelectFields.forEach((field) => {
      const value = getQueryValue(filters[field]);
      if (value !== undefined) {
        query[field] = value;
      }
    });

    if (Array.isArray(filters.tagTypes) && filters.tagTypes.length > 0) {
      query.tagTypes = filters.tagTypes.map((t) => t.value);
    }

    if (filters.filterField?.value && filters.filterValue) {
      const fieldMap = {
        City: "rCity",
      };
      const field =
        fieldMap[filters.filterField.value] || filters.filterField.value;

      query[field] = filters.exactMatch
        ? filters.filterValue
        : { $like: `%${filters.filterValue}%` };
    }

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_EMPLOYEES_REQUEST,
        success: FETCH_EMPLOYEES_SUCCESS,
        failure: FETCH_EMPLOYEES_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (res) => ({
        employees: res.data,
        total: res.total,
      }),
      errorMessagePrefix: "Failed to fetch employees",
    });
  };

export const createEmployee = (employeeData) => async (dispatch) => {
  const service = getService(serviceNames.personnel);
  try {
    if (employeeData && employeeData.employeeNum) {
      const result = await service.find({
        query: { employeeNum: employeeData.employeeNum },
      });

      if (result.total > 0) {
        return { duplicate: true };
      }
    }

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: CREATE_EMPLOYEE_REQUEST,
        success: CREATE_EMPLOYEE_SUCCESS,
        failure: CREATE_EMPLOYEE_FAILURE,
      },
      apiCall: () => service.create(employeeData),
      successMessage: "Employee created successfully",
      errorMessagePrefix: "Failed to create employee",
    });
  } catch (err) {
    dispatch({
      type: CREATE_EMPLOYEE_FAILURE,
      payload: err.message,
    });
    return null;
  }
};

export const updateEmployee = (id, employeeData) => async (dispatch) => {
  const service = getService(serviceNames.personnel);
  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: UPDATE_EMPLOYEE_REQUEST,
      success: UPDATE_EMPLOYEE_SUCCESS,
      failure: UPDATE_EMPLOYEE_FAILURE,
    },
    apiCall: () => service.patch(id, employeeData),
    successMessage: "Employee updated successfully",
    errorMessagePrefix: "Failed to update employee",
  });
};

export const deleteEmployee = (id, userId) => async (dispatch) => {
  const service = getService(serviceNames.personnel);
  const patchData = {
    isDeleted: true,
    deletedBy: userId,
    deletedAt: new Date().toISOString(),
  };

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: DELETE_EMPLOYEE_REQUEST,
      success: DELETE_EMPLOYEE_SUCCESS,
      failure: DELETE_EMPLOYEE_FAILURE,
    },
    apiCall: () => service.patch(id, patchData),
    transformResult: () => id,
    successMessage: "Employee deleted successfully",
    errorMessagePrefix: "Failed to delete employee",
  });
};

export default function employeeReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_EMPLOYEES_REQUEST:
    case CREATE_EMPLOYEE_REQUEST:
    case UPDATE_EMPLOYEE_REQUEST:
    case DELETE_EMPLOYEE_REQUEST:
      return { ...state, loading: true, error: null };
    case FETCH_EMPLOYEES_SUCCESS:
      return {
        ...state,
        loading: false,
        employees: action.payload.employees,
        total: action.payload.total,
      };
    case CREATE_EMPLOYEE_SUCCESS:
      return {
        ...state,
        loading: false,
        employees: [...state.employees, action.payload],
        total: state.total + 1,
      };
    case UPDATE_EMPLOYEE_SUCCESS:
      return {
        ...state,
        loading: false,
        employees: state.employees.map((emp) =>
          emp.id === action.payload.id ? action.payload : emp
        ),
      };
    case DELETE_EMPLOYEE_SUCCESS:
      return {
        ...state,
        loading: false,
        employees: state.employees.filter((emp) => emp.id !== action.payload),
        total: state.total - 1,
      };
    case FETCH_EMPLOYEES_FAILURE:
    case CREATE_EMPLOYEE_FAILURE:
    case UPDATE_EMPLOYEE_FAILURE:
    case DELETE_EMPLOYEE_FAILURE:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}
